import React, {createContext, useContext, useState, useEffect, ReactNode} from 'react';
import {Appearance, ColorSchemeName} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {setDarkMode, lightTheme, darkTheme} from '~constants/colors';

type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeContextType {
  isDarkMode: boolean;
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;
  colors: typeof lightTheme | typeof darkTheme;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

const THEME_STORAGE_KEY = '@pyxi_theme_mode';

export const ThemeProvider: React.FC<ThemeProviderProps> = ({children}) => {
  const [themeMode, setThemeModeState] = useState<ThemeMode>('light');
  const [systemColorScheme, setSystemColorScheme] = useState<ColorSchemeName>(Appearance.getColorScheme());

  // Calculate if dark mode should be active
  const isDarkMode = React.useMemo(() => {
    let result: boolean;
    switch (themeMode) {
      case 'dark':
        result = true;
        break;
      case 'light':
        result = false;
        break;
      case 'system':
        result = systemColorScheme === 'dark';
        break;
      default:
        result = false; // Default to light mode for any unknown theme mode
        break;
    }
    return result;
  }, [themeMode, systemColorScheme]);

  // Get current colors based on theme
  const colors = React.useMemo(() => {
    return isDarkMode ? darkTheme : lightTheme;
  }, [isDarkMode]);

  // Load saved theme preference on mount
  useEffect(() => {
    const loadThemePreference = async () => {
      try {
        const savedTheme = await AsyncStorage.getItem(THEME_STORAGE_KEY);
        if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
          setThemeModeState(savedTheme as ThemeMode);
        }
      } catch (error) {
        console.warn('Failed to load theme preference:', error);
      }
    };

    loadThemePreference();
  }, []);

  // Listen for system theme changes
  useEffect(() => {
    const subscription = Appearance.addChangeListener(({colorScheme}) => {
      setSystemColorScheme(colorScheme);
    });

    return () => subscription.remove();
  }, []);

  // Update global color system when theme changes
  useEffect(() => {
    setDarkMode(isDarkMode);
  }, [isDarkMode]);

  // Save theme preference and update state
  const setThemeMode = React.useCallback(async (mode: ThemeMode) => {
    try {
      await AsyncStorage.setItem(THEME_STORAGE_KEY, mode);
      setThemeModeState(mode);
    } catch (error) {
      console.warn('Failed to save theme preference:', error);
      // Still update state even if storage fails
      setThemeModeState(mode);
    }
  }, []);

  // Toggle between light and dark (ignoring system)
  const toggleTheme = React.useCallback(() => {
    const newMode = isDarkMode ? 'light' : 'dark';
    setThemeMode(newMode);
  }, [isDarkMode, setThemeMode]);

  const value: ThemeContextType = React.useMemo(
    () => ({
      isDarkMode,
      themeMode,
      setThemeMode,
      toggleTheme,
      colors,
    }),
    [isDarkMode, themeMode, setThemeMode, toggleTheme, colors],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Hook for getting theme-aware styles
export const useThemedStyles = <T extends Record<string, any>>(
  styleFactory: (colors: typeof lightTheme | typeof darkTheme, isDarkMode: boolean) => T,
): T => {
  const {colors, isDarkMode} = useTheme();
  return React.useMemo(() => styleFactory(colors, isDarkMode), [colors, isDarkMode, styleFactory]);
};

// Hook for conditional rendering based on theme
export const useThemeConditional = <T,>(lightValue: T, darkValue: T): T => {
  const {isDarkMode} = useTheme();
  return isDarkMode ? darkValue : lightValue;
};

export default ThemeContext;
